/**
 * opportunityChart — pure data-shaping utils for the L2 finding-page charts
 * (Lane 47 Amendment A1; Consult #4 resolved by Ryan 2026-07-02).
 *
 * DISPLAY SHAPING ONLY: nothing here computes a dollar claim; prices pass
 * through verbatim (outliers capped exactly as the price-history endpoint
 * caps them), the market comparison is % change from a COMMON anchor month,
 * and dates are windowed. Each transform lives HERE once (plan §6F) and is
 * unit-tested in opportunityChart.test.js.
 */

/**
 * Normalize a supplier name to the engine's durable supplier_key: collapse
 * internal whitespace to single spaces, trim, uppercase.
 *
 * PINNED MIRROR of the Python engine's normalization —
 * backend/helpers/opportunities.py:normalize_supplier_key — which itself must
 * agree with the SQL `UPPER(TRIM(regexp_replace(name, '\s+', ' ', 'g')))`.
 * The unit tests use the SAME golden cases as the engine's
 * test_normalize_supplier_key_collapses_and_uppercases, so any drift between
 * the two rules fails a test instead of mis-highlighting the chart (Lane 47
 * Section 2A condition, Ryan 2026-07-02).
 */
export function normalizeSupplierKey(name) {
  if (name === null || name === undefined) return '';
  return String(name).replace(/\s+/g, ' ').trim().toUpperCase();
}

/**
 * Parse a date string to a LOCAL-timezone epoch (ms). Date-only strings
 * ('2025-10-03') and month-only strings ('2025-10') are parsed as LOCAL
 * dates — new Date('2025-10-03') would parse as UTC midnight and shift a day
 * in US timezones (same caution as opportunityDisplay.formatDate).
 * Returns null for unparseable input.
 */
export function toLocalEpoch(iso) {
  if (!iso) return null;
  const s = String(iso);
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])).getTime();
  }
  const ym = /^(\d{4})-(\d{2})$/.exec(s);
  if (ym) {
    return new Date(Number(ym[1]), Number(ym[2]) - 1, 1).getTime();
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/** The epoch (ms) exactly `months` calendar months before asOf (a Date). */
export function monthsBefore(asOf, months) {
  const d = new Date(asOf.getFullYear(), asOf.getMonth() - months, asOf.getDate());
  return d.getTime();
}

// Same-day window defaults (Bug A / Ryan option ii, 2026-07-03).
export const DEFAULT_SAME_DAY_WINDOW = 18;   // A3's deliberate 18mo for volume + same-day
export const SAME_DAY_CONTEXT_BUFFER = 2;    // months of context before an old incident

/**
 * The window ANCHOR ("as of") the L2 chart, PO table, and comparison all measure
 * back from. For SAME-DAY findings we anchor to the SNAPSHOT's frozen date
 * (finding.created_at) so the window is stable as real time passes AND the
 * widened window (evidenceWindowMonths) actually reaches the incident — the
 * length math and the chart measure from the SAME anchor. Volume + dispersion
 * keep the browser-today anchor (byte-identical behavior). Falls back to today
 * if created_at is absent/unparseable.
 */
export function chartAsOf(finding) {
  if (finding?.family_type === 'same_day' && finding?.created_at) {
    const d = new Date(finding.created_at);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

/**
 * The ONE evidence-window length (in months) for a finding's L2 page — the
 * chart, PO table, and comparison all derive from this single value (A3 item 3).
 *  - dispersion: the finding's FROZEN window_months.typical (24 today).
 *  - volume: the fixed 18mo (A3's 523104 step-up rationale).
 *  - same-day (Bug A / Ryan option ii): 18mo for a RECENT incident (unchanged),
 *    but widened JUST ENOUGH to always include an OLDER incident + a context
 *    buffer. Same-day incidents come from the detector's 24-month pool, so a
 *    fixed 18mo window can fall short of the very day the finding describes
 *    (273142's Dec 2024 incident vs a Jul 2026 snapshot). The inclusion check
 *    uses the SAME monthsBefore + anchor the chart uses, so "inside the window"
 *    here means exactly "on the chart."
 * asOf defaults to chartAsOf(finding) so callers can pass the shared anchor.
 */
export function evidenceWindowMonths(finding, asOf) {
  // Lane 338: step_change joins dispersion on the frozen typical window (24mo)
  // — the default 18mo window would clip the base plateau on a step that has
  // held 20+ months (the flagship shape). Display-only window selection.
  if (finding?.family_type === 'dispersion' || finding?.family_type === 'step_change') {
    return ((finding.detail_json || {}).window_months || {}).typical || 24;
  }
  if (finding?.family_type === 'same_day') {
    const anchor = asOf || chartAsOf(finding);
    const incidentIso = (finding.detail_json || {}).incident_date || finding.incident_date;
    // Parse via toLocalEpoch (NOT new Date()) so a date-only 'YYYY-MM-DD' string
    // is LOCAL, matching monthsBefore + the chart's own windowing — a raw
    // new Date('2025-01-03') would be UTC midnight and shift the boundary a day.
    const incidentEpoch = toLocalEpoch(incidentIso);
    if (incidentEpoch === null) return DEFAULT_SAME_DAY_WINDOW;
    // If the default 18mo window already reaches the incident, change nothing.
    if (incidentEpoch >= monthsBefore(anchor, DEFAULT_SAME_DAY_WINDOW)) {
      return DEFAULT_SAME_DAY_WINDOW;
    }
    // Older: widen to the incident's whole-month age (rounded UP so the window
    // start clears the incident's own day) + a context buffer.
    const incident = new Date(incidentEpoch);   // local Date built from the local epoch
    let ageMonths = (anchor.getFullYear() - incident.getFullYear()) * 12
      + (anchor.getMonth() - incident.getMonth());
    if (anchor.getDate() < incident.getDate()) ageMonths += 1;
    return ageMonths + SAME_DAY_CONTEXT_BUFFER;
  }
  return DEFAULT_SAME_DAY_WINDOW;  // volume
}

/** 'YYYY-MM' month key for a date string (local parse); null if unparseable. */
export function monthKey(iso) {
  const x = toLocalEpoch(iso);
  if (x === null) return null;
  const d = new Date(x);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * Chart 1 data — a faithful clone of the MaterialDossier "Price Over Time by
 * Supplier" shaping (MaterialDossier.js ~670-709), applied to the Lane 47
 * trailing window. Returns { lineData, suppliers, windowOutliers, yDomain }.
 *
 * Clone fidelity notes (Amendment A1 / Consult #4 pins):
 *  - lineData rows are one-per-DATE with a 'MMM YY' label, one column per
 *    supplier; outlier POs plot at their CAPPED price with the actual price
 *    kept in row._outliers (the dossier's amber-open-circle contract).
 *  - windowOutliers + yDomain are computed on the WINDOW SLICE, not full
 *    history (Consult #4 pin (a)) — an out-of-window outlier can neither
 *    inflate the footnote nor stretch the axis.
 *  - Same-day rule: multiple same-day purchases by one supplier keep the
 *    day's LOWEST price (Ryan-locked, Consult #4 resolution) — a deliberate,
 *    documented deviation from the dossier's last-write-wins overwrite,
 *    pinned by test.
 */
export function buildDossierRows(records, asOf = new Date(), windowMonths = 18) {
  const start = monthsBefore(asOf, windowMonths);
  const end = asOf.getTime();

  const valid = (records || []).filter((r) => {
    const price = Number(r.price ?? r.unit_price);
    const x = toLocalEpoch(r.date || r.po_date);
    return Number.isFinite(price) && price > 0 && x !== null && x >= start && x <= end;
  });

  const suppliers = [...new Set(valid.map((r) => r.supplier || r.supplier_name))]
    .filter(Boolean);

  const byDate = {};
  const windowOutliers = [];
  for (const r of valid) {
    const dateStr = r.date || r.po_date;
    const supplierKey = r.supplier || r.supplier_name;
    const price = Number(r.price ?? r.unit_price);
    const label = new Date(toLocalEpoch(dateStr)).toLocaleDateString('en-US', {
      month: 'short', year: '2-digit',
    });
    if (!byDate[dateStr]) byDate[dateStr] = { date: dateStr, label, _outliers: {}, _qty: {}, _pos: {} };
    const row = byDate[dateStr];
    // Tooltip volume (A2.2): the day's TOTAL units per supplier — same-day
    // orders sum, so the collapsed single-PO dot is honest about volume.
    const qty = Number(r.quantity ?? r.purchase_quantity);
    const poQty = Number.isFinite(qty) && qty > 0 ? qty : null;
    if (poQty !== null) {
      row._qty[supplierKey] = (row._qty[supplierKey] || 0) + poQty;
    }
    // Per-PO capture (Bug B, 2026-07-03): every PO's ACTUAL price + its own
    // quantity for this (date, supplier), so a shared-day tooltip can list each
    // PO one-to-one with the PO table. The plotted dot still uses the day's
    // LOWEST (below) — this only enriches the tooltip; capped points store their
    // real UNCAPPED price here, which the tooltip shows beside the "excluded
    // from scale" marker (Lane 824 re-worded that marker from "flagged unusual":
    // `is_outlier` is the price-history IQR statistic, not the finding's own
    // evidence, so it may state the SCALE fact but must not name a PO unusual —
    // that is the finding card's job, and only its).
    if (!row._pos[supplierKey]) row._pos[supplierKey] = [];
    row._pos[supplierKey].push({ price, quantity: poQty, is_outlier: Boolean(r.is_outlier) });
    if (r.is_outlier) {
      // Dossier-equivalent fallback (`capped_price || price`), made explicit:
      // a missing/zero/NaN cap falls back to the raw price — a malformed cap
      // can never plot as a NaN gap (/review fix, 2026-07-02).
      const cap = Number(r.capped_price);
      const plotted = Number.isFinite(cap) && cap > 0 ? cap : price;
      row[supplierKey] = row[supplierKey] === undefined
        ? plotted : Math.min(row[supplierKey], plotted);
      row._outliers[supplierKey] = price;
      windowOutliers.push({ supplier: supplierKey, date: dateStr, price });
    } else {
      // Same-day rule: keep the day's LOWEST (Ryan-locked; see docstring).
      row[supplierKey] = row[supplierKey] === undefined
        ? price : Math.min(row[supplierKey], price);
    }
  }
  const lineData = Object.values(byDate)
    .sort((a, b) => toLocalEpoch(a.date) - toLocalEpoch(b.date));

  // Y-domain from NON-outlier window prices only, +10% pad (dossier rule).
  const nonOutlierPrices = valid.filter((r) => !r.is_outlier)
    .map((r) => Number(r.price ?? r.unit_price)).filter((v) => v > 0);
  const yMin = nonOutlierPrices.length ? Math.min(...nonOutlierPrices) : 0;
  const yMax = nonOutlierPrices.length ? Math.max(...nonOutlierPrices) : 100;
  const pad = (yMax - yMin) * 0.1 || 1;
  const yDomain = [Math.max(0, yMin - pad), yMax + pad];

  return { lineData, suppliers, windowOutliers, yDomain };
}

/**
 * Chart 2 data — "Your price vs the market" (Amendment A2.6 + Consult #4/#5):
 *  - "Your price" = the FINDING'S SUPPLIER's monthly average unit price for
 *    this material (normalized-name match), OUTLIER-CAPPED: an is_outlier PO
 *    contributes its capped_price (or is excluded when no cap was provided),
 *    so one UOM-error purchase cannot warp the line.
 *  - The MARKET line is the SUPPLIER's blended market index — the EXACT
 *    series the supplier dossier renders (supplier-price-impact
 *    ppi_data_points[].blended_ppi_index, a % change vs the blend's own
 *    baseline). Consult #5 pin BP-a: those %-changes are converted to index
 *    LEVELS (100 + pct) BEFORE any positivity filtering — the raw pct is
 *    0.00 at the blend's own base month and legitimately negative in soft
 *    markets, and must never be dropped as "non-positive".
 *  - BOTH series are % change re-anchored at the FIRST COMMON in-window
 *    month (Consult #4): market drift before your first purchase can never
 *    appear as a phantom gap. Level ratios are base-invariant, so the
 *    re-anchoring is mathematically exact against the dossier's series.
 *  - The blend emits points only for months its own inputs cover — NEITHER
 *    series is guaranteed monthly-complete; connectNulls bridges gaps
 *    (Consult #5 pin BP-c).
 *
 * blendPoints: [{month: 'YYYY-MM', blended_ppi_index: pct}, ...]
 * Returns { rows: [{month, monthLabel, your_price_index, ppi_index}],
 *           anchorMonth } or null when no common month exists (Chart 2 is
 *   then absent — the caller shows the quiet no-benchmark note).
 */
export function buildIndexedComparisonRows(records, blendPoints, supplierDisplay,
                                           asOf = new Date(), windowMonths = 18) {
  const start = monthsBefore(asOf, windowMonths);
  const end = asOf.getTime();
  const hlKey = normalizeSupplierKey(supplierDisplay);
  if (hlKey === '') return null;

  // Supplier's monthly averages, outlier-capped.
  const sums = new Map();   // month -> {total, count}
  for (const r of records || []) {
    const supplier = r.supplier || r.supplier_name;
    if (normalizeSupplierKey(supplier) !== hlKey) continue;
    const x = toLocalEpoch(r.date || r.po_date);
    if (x === null || x < start || x > end) continue;
    let price = Number(r.price ?? r.unit_price);
    if (r.is_outlier) {
      if (r.capped_price === null || r.capped_price === undefined) continue;
      price = Number(r.capped_price);
    }
    if (!Number.isFinite(price) || price <= 0) continue;
    const mk = monthKey(r.date || r.po_date);
    const acc = sums.get(mk) || { total: 0, count: 0 };
    acc.total += price;
    acc.count += 1;
    sums.set(mk, acc);
  }
  const monthlyAvg = new Map();
  for (const [mk, acc] of sums) monthlyAvg.set(mk, acc.total / acc.count);

  // Market monthly LEVELS in window (BP-a: pct -> level FIRST; a level is
  // positive whenever pct > -100, so 0.00 and negative pcts survive).
  const ppiByMonth = new Map();
  for (const p of blendPoints || []) {
    const x = toLocalEpoch(p.month);
    const pct = Number(p.blended_ppi_index);
    if (x === null || x < start || x > end || !Number.isFinite(pct)) continue;
    const level = 100 + pct;
    if (level <= 0) continue;
    ppiByMonth.set(monthKey(p.month), level);
  }

  // First COMMON month anchors BOTH series (Consult #4).
  const commonMonths = [...monthlyAvg.keys()].filter((m) => ppiByMonth.has(m)).sort();
  if (commonMonths.length === 0) return null;
  const anchorMonth = commonMonths[0];
  const basePrice = monthlyAvg.get(anchorMonth);
  const basePpi = ppiByMonth.get(anchorMonth);
  if (!(basePrice > 0) || !(basePpi > 0)) return null;

  // Plot from the anchor onward: every month with EITHER value.
  const allMonths = [...new Set([...monthlyAvg.keys(), ...ppiByMonth.keys()])]
    .filter((m) => m >= anchorMonth).sort();
  const rows = allMonths.map((m) => ({
    month: m,
    monthLabel: new Date(toLocalEpoch(m)).toLocaleDateString('en-US', {
      month: 'short', year: '2-digit',
    }),
    your_price_index: monthlyAvg.has(m)
      ? ((monthlyAvg.get(m) / basePrice) - 1) * 100 : null,
    ppi_index: ppiByMonth.has(m)
      ? ((ppiByMonth.get(m) / basePpi) - 1) * 100 : null,
  }));
  return { rows, anchorMonth };
}

/**
 * The L2 purchase-history table (A2.1): per-PO rows on the SAME trailing
 * window the charts use, newest first. NOTHING is collapsed — same-day
 * same-price orders each keep their own row (order-cadence/consolidation
 * evidence; the engine-side same-day clustering detector remains the filed
 * v1.1 item).
 */
export function windowPurchases(records, asOf = new Date(), windowMonths = 18) {
  const start = monthsBefore(asOf, windowMonths);
  const end = asOf.getTime();
  return (records || [])
    .filter((r) => {
      const x = toLocalEpoch(r.date || r.po_date);
      const price = Number(r.price ?? r.unit_price);
      return x !== null && x >= start && x <= end
        && Number.isFinite(price) && price > 0;
    })
    .map((r) => ({
      date: r.date || r.po_date,
      supplier: r.supplier || r.supplier_name || '-',
      price: Number(r.price ?? r.unit_price),
      quantity: Number(r.quantity ?? r.purchase_quantity) || null,
      is_outlier: Boolean(r.is_outlier),
    }))
    .sort((a, b) => toLocalEpoch(b.date) - toLocalEpoch(a.date));
}
